import { AcceptInvitationForm, AdminSetNames } from "@closerplatform/spinner-openapi";
import { FieldState, FormState } from "formstate";
import { inject, observer } from "mobx-react";
import React, { FormEvent } from "react";
import { AgentInput } from "@fidget/common/components/form/agentInput";
import { L10n, L10nHtml } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { parseQueryString } from "@fidget/common/utils";
import { scrollToSection } from "@fidget/common/utils/scrollToSection";
import {
  PasswordValidation,
  validatePasswordPolicy
} from "@fidget/common/utils/validatePasswordPolicy";
import { validatePassword, validateRequired } from "@fidget/common/utils/validatorsFormState";
import { ICurrentUserStore } from "../../../models/currentUser/CurrentUserStore";
import { PasswordRules } from "../../passwordRules";
import { BackLinkContainer } from "../components/backLinkContainer";
import { BeforeLoginSection } from "../components/beforeLoginSection";
import { LoginInstead } from "../components/loginInstead";
import { ErrorSection } from "./errorSection";
import { SetUpSection } from "./setUpSection";
import styles from "./signUpCommon.css";
import { TokenExpiredSection } from "./tokenExpiredSection";

type PasswordFormFields = {
  password: FieldState<string>;
  passwordRepeated: FieldState<string>;
};

type NameFormFields = {
  firstName: FieldState<string>;
  lastName: FieldState<string>;
};

interface Props {
  currentUserStore?: ICurrentUserStore;
  timeout: number;
}

interface State {
  displayMode: "completingAccount" | "setUp" | "invitationExpired" | "error";
  passwordValidation: PasswordValidation;
}

@inject("currentUserStore")
@observer
export class AcceptInvitation extends React.Component<Props, State> {
  token?: string;
  email?: string;
  passwordFields: PasswordFormFields;
  passwordForm: FormState<PasswordFormFields>;
  nameFields: NameFormFields;
  nameForm: FormState<NameFormFields>;
  focusedSectionNumber: number;
  styleType: "signup" = "signup";

  constructor(props: Props) {
    super(props);
    this.state = {
      displayMode: "completingAccount",
      passwordValidation: {
        hasOneNumber: false,
        hasUppercase: false,
        longEnough: false
      }
    };

    this.passwordFields = {
      password: new FieldState("")
        .disableAutoValidation()
        .validators(validateRequired, validatePassword),
      passwordRepeated: new FieldState("")
        .disableAutoValidation()
        .validators(this.validatePasswordRepeated)
    };
    this.passwordForm = new FormState(this.passwordFields).validators(($) =>
      $.password.$ !== $.passwordRepeated.$
        ? intl.formatMessage("passwordValidation.passMismatch")
        : ""
    );

    this.nameFields = {
      firstName: new FieldState("").disableAutoValidation().validators(validateRequired),
      lastName: new FieldState("").disableAutoValidation().validators(this.validateLastNameRequired)
    };
    this.nameForm = new FormState(this.nameFields);
    this.focusedSectionNumber = 0;
  }

  validateLastNameRequired = (val: string) => {
    const { firstName } = this.nameFields;
    const nameForError =
      firstName.value.length > 10 ? firstName.value.substring(0, 10) + "..." : firstName.value;
    return (
      !val &&
      `${intl.formatMessage("error.fieldRequired")}
    ${firstName.value
        ? intl.formatMessage("signup.name.error.whatsLastName.withName", { name: nameForError })
        : ""
      }`
    );
  };

  componentDidMount() {
    const { currentUserStore } = this.props;
    const { email, token } = parseQueryString();
    this.token = token;
    this.email = email;
    currentUserStore!
      .verifyUserInvitationByToken(token!)
      .then((inviteeNames: AdminSetNames) => {
        this.nameFields.firstName.value = inviteeNames.firstName || "";
        this.nameFields.lastName.value = inviteeNames.lastName || "";
      })
      .catch(() => this.setState({ displayMode: "invitationExpired" }));
  }

  validatePasswordRepeated = (val: string) => {
    const { password } = this.passwordFields;
    return (
      !password.hasError &&
      val !== password.value &&
      intl.formatMessage("passwordValidation.passMismatch")
    );
  };

  handlePasswordChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ passwordValidation: validatePasswordPolicy(ev.target.value) });
  };

  handleScrollSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const result = await this.nameForm.validate();
    if (!result.hasError) {
      scrollToSection(++this.focusedSectionNumber);
    }
  };

  handleJoinTeamSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const { currentUserStore, timeout } = this.props;
    const form: FormState<any> = new FormState([this.passwordForm, this.nameForm]);
    try {
      const { hasError } = await form.validate();
      if (!hasError) {
        const acceptInvitationForm: AcceptInvitationForm = {
          firstName: this.nameForm.$.firstName.value,
          lastName: this.nameForm.$.lastName.value,
          password: this.passwordForm.$.password.value
        };
        this.setState({ displayMode: "setUp" });

        try {
          const profile = await currentUserStore!.acceptInvitation(
            this.token || "",
            acceptInvitationForm
          );
          setTimeout(() => currentUserStore!.authenticateProfile(profile), timeout);
        } catch {
          setTimeout(() => this.setState({ displayMode: "error" }), timeout);
        }
      }
    } catch {
      setTimeout(() => this.setState({ displayMode: "error" }), timeout);
    }
  };

  renderCompletingAccount = () => {
    const { firstName, lastName } = this.nameFields;
    const { password, passwordRepeated } = this.passwordFields;
    const howToAddressString =
      firstName.value || lastName.value
        ? intl.formatMessage("signup.name.isThatHowAddress")
        : intl.formatMessage("signup.name.howAddress");

    return (
      <>
        <BeforeLoginSection styleType={this.styleType}>
          <header>
            <h2>{howToAddressString}</h2>
          </header>
          <form onSubmit={this.handleScrollSubmit}>
            <AgentInput
              field={firstName}
              styleType={this.styleType}
              placeholder={intl.formatMessage("signup.name.placeholder.whatsName")}
              maxLength={64}
              autoFocus
            />
            <AgentInput
              field={lastName}
              styleType={this.styleType}
              placeholder={intl.formatMessage("signup.name.placeholder.whatsLastName")}
              maxLength={64}
            />
            <p>
              <L10n id={"signup.name.description"} />
            </p>
            <div className={styles.pushdown}>
              <button disabled={!firstName.value || !lastName.value} type={"submit"}>
                <L10n id={"signup.continue"} />
              </button>
            </div>
          </form>
          <LoginInstead />
        </BeforeLoginSection>
        <BeforeLoginSection styleType={this.styleType}>
          <header>
            <h2>
              <L10n id={"signup.password.pickPassword"} />
            </h2>
            <PasswordRules passwordValidation={this.state.passwordValidation} okColor={"#107377"} />
          </header>
          <form onSubmit={this.handleJoinTeamSubmit}>
            <AgentInput
              styleType={this.styleType}
              field={password}
              type={"password"}
              placeholder={intl.formatMessage("signup.password.placeholder.password")}
              onChange={this.handlePasswordChange}
              autoComplete={"new-password"}
            />
            <AgentInput
              styleType={this.styleType}
              field={passwordRepeated}
              type={"password"}
              placeholder={intl.formatMessage("signup.password.placeholder.repeatPassword")}
              autoComplete={"new-password"}
            />
            <div className={styles.pushdown}>
              <p>
                <L10nHtml id={"signup.password.youEmail"} values={{ email: this.email }} />
              </p>
              <button disabled={!(password.value && passwordRepeated.value)} type={"submit"}>
                <L10n id={"signup.joinTheTeam"} />
              </button>
            </div>
          </form>
          <BackLinkContainer onClick={() => scrollToSection(--this.focusedSectionNumber, true)} />
        </BeforeLoginSection>
      </>
    );
  };

  render() {
    switch (this.state.displayMode) {
      case "invitationExpired":
        return <TokenExpiredSection version={"agent"} />;
      case "completingAccount":
        return this.renderCompletingAccount();
      case "error":
        return <ErrorSection />;
      case "setUp":
        return <SetUpSection timeout={this.props.timeout} />;
      default:
        return <ErrorSection />;
    }
  }
}
